import React from "react";
import {Field} from "formik";
import {MenuItem, TextField, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import IconPicker from "../shared/IconPicker.js";

const NATURES = [
    {value: "LAVAGE", label: "Lavage (acte par défaut)"},
    {value: "BOOLEEN", label: "Booléen (oui / non)"},
    {value: "CHECKLIST", label: "Checklist (zones à inspecter)"},
];

const PHOTO_REGLES = [
    {value: "JAMAIS", label: "Jamais"},
    {value: "SI_POSITIF", label: "Si positif / problème"},
    {value: "TOUJOURS", label: "Toujours"},
];

/**
 * Form acte (patron maison). La gestion des éléments de maintenance (zones) se fait
 * à part (ElementMaintenanceField), en édition et seulement si nature = CHECKLIST.
 */
export default function ActeForm({values, errors, touched, handleChange, handleBlur, setFieldValue, modesOperatoires = []}) {
    const row = (label, required, children) => (
        <Grid item container size={12} alignItems={"center"}>
            <Grid size={3}>{label} {required && <span className="asterix">*</span>}</Grid>
            <Grid size={9}>{children}</Grid>
        </Grid>
    );

    const textField = (name, label, helper) => (
        <Field type="text" value={values[name]}>
            {({form}) => (
                <TextField
                    error={Boolean(form.errors[name] && form.touched[name])}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values[name] ?? ""}
                    variant="outlined"
                    margin="normal"
                    size="small"
                    fullWidth
                    name={name}
                    label={label}
                    helperText={(form.errors[name] && form.touched[name] && String(form.errors[name])) || helper}
                />
            )}
        </Field>
    );

    return (
        <Grid container spacing={1} className={"sectionContainer"}>
            <Grid size={12}>
                <Typography variant={"h5"} className={"sectionHeader"}>Informations</Typography>
            </Grid>

            {row("Code", true, textField("code", "Code", "Identifiant stable (ex. GRAFFITI, MAINT_PREV)"))}
            {row("Libellé (FR)", true, textField("libelleFr", "Libellé (FR)"))}
            {row("Libellé (AR)", false,
                <Field type="text" value={values.libelleAr}>
                    {({form}) => (
                        <TextField
                            onChange={handleChange} onBlur={handleBlur} value={values.libelleAr ?? ""}
                            variant="outlined" margin="normal" size="small" fullWidth dir="rtl"
                            name="libelleAr" label="Libellé (AR)"
                        />
                    )}
                </Field>
            )}

            {row("Nature", true,
                <TextField
                    select fullWidth size="small" margin="normal" variant="outlined"
                    name="nature" label="Nature" value={values.nature ?? "BOOLEEN"}
                    onChange={handleChange} onBlur={handleBlur}
                >
                    {NATURES.map((n) => <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>)}
                </TextField>
            )}

            {row("Règle photo", true,
                <TextField
                    select fullWidth size="small" margin="normal" variant="outlined"
                    name="photoRegle" label="Règle photo" value={values.photoRegle ?? "SI_POSITIF"}
                    onChange={handleChange} onBlur={handleBlur}
                >
                    {PHOTO_REGLES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
            )}

            {row("Photos minimum", false,
                <TextField
                    type="number" size="small" margin="normal" variant="outlined"
                    name="photoMin" label="Photos minimum" value={values.photoMin ?? 1}
                    onChange={handleChange} onBlur={handleBlur}
                    inputProps={{min: 0}} sx={{width: 160}}
                />
            )}

            {row("Mode opératoire", false,
                <TextField
                    select fullWidth size="small" margin="normal" variant="outlined"
                    name="modeOperatoire" label="Mode opératoire (bouton « ? »)"
                    value={values.modeOperatoire ?? ""}
                    onChange={(e) => setFieldValue("modeOperatoire", e.target.value || null)}
                    helperText="Optionnel — affiché via le bouton « ? » de l'acte côté mobile."
                >
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {[...modesOperatoires]
                        .sort((a, b) => (a.libelle || "").localeCompare(b.libelle || ""))
                        .map((m) => <MenuItem key={m["@id"]} value={m["@id"]}>{m.libelle}</MenuItem>)}
                </TextField>
            )}

            {row("Icône", true,
                <IconPicker
                    showLabel={false}
                    value={values.picto}
                    color={"#11285e"}
                    onChange={(key) => setFieldValue("picto", key)}
                    error={touched.picto && errors.picto}
                />
            )}
        </Grid>
    );
}
